import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, formatApiErrorDetail, useAuth } from '../../lib/api';
import { formatCurrency, getAttachRateColor } from '../../lib/utils';
import { AppLayout } from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  ShoppingCart,
  ShieldCheck,
  TrendingUp,
  ArrowRight,
  Loader2,
  Users,
} from 'lucide-react';
import { toast } from 'sonner';

export default function StoreManagerDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: dashboardData } = await dashboardApi.storeManager();
        setData(dashboardData);
      } catch (error) {
        toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Failed to load dashboard');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-[#005DE8]" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            Branch Dashboard
          </h1>
          <p className="text-slate-600 mt-1">Welcome back, {user?.name}</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Daily Sales */}
          <Card className="border-slate-200" data-testid="kpi-daily-sales">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Today's Sales</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {data?.daily?.sales || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <ShoppingCart className="h-6 w-6 text-[#005DE8]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Daily Warranties */}
          <Card className="border-slate-200" data-testid="kpi-daily-warranties">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Today's Warranties</p>
                  <p className="text-3xl font-bold text-emerald-600 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {data?.daily?.warranties || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Monthly Sales */}
          <Card className="border-slate-200" data-testid="kpi-monthly-sales">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Monthly Sales</p>
                  <p className="text-3xl font-bold text-slate-900 mt-2" style={{ fontFamily: 'Outfit' }}>
                    {data?.monthly?.sales || 0}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    {data?.monthly?.warranties || 0} with warranty
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-50 rounded-xl flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Warranty Revenue */}
          <Card className="border-slate-200" data-testid="kpi-warranty-revenue">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">W. Revenue</p>
                  <p className="text-3xl font-bold text-[#005DE8] mt-2" style={{ fontFamily: 'Outfit' }}>
                    {formatCurrency(data?.warranty_revenue || 0)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">This month</p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <ShieldCheck className="h-6 w-6 text-[#005DE8]" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attach Rate */}
          <Card className="border-slate-200" data-testid="kpi-attach-rate">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">Attach Rate</p>
                  <p className={`text-3xl font-bold mt-2 ${getAttachRateColor(data?.monthly?.attach_rate || 0)}`} style={{ fontFamily: 'Outfit' }}>
                    {data?.monthly?.attach_rate || 0}%
                  </p>
                  <div className="mt-3">
                    <Progress value={data?.monthly?.attach_rate || 0} className="h-2" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Staff Performance */}
        <Card className="border-slate-200" data-testid="staff-performance-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold flex items-center gap-2" style={{ fontFamily: 'Outfit' }}>
                <Users className="h-5 w-5 text-[#005DE8]" />
                Staff Performance
              </CardTitle>
              <Link to="/manager/staff">
                <Button variant="ghost" size="sm" className="text-[#005DE8]">
                  View All
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {data?.staff_performance?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-100">
                    <TableHead>Staff</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Achieved</TableHead>
                    <TableHead>Progress</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.staff_performance.map((staff) => (
                    <TableRow key={staff.user_id} className="table-row-hover">
                      <TableCell className="font-medium">{staff.name}</TableCell>
                      <TableCell className="text-right">{staff.target}</TableCell>
                      <TableCell className="text-right font-semibold">{staff.achieved}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={Math.min(staff.achievement_percent, 100)} className="h-2 flex-1" />
                          <span className={`text-sm font-medium ${getAttachRateColor(staff.achievement_percent)}`}>
                            {staff.achievement_percent}%
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                No staff data available
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Link to="/manager/sales">
                <Button variant="outline" data-testid="action-view-sales">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  View Sales
                </Button>
              </Link>
              <Link to="/manager/staff">
                <Button variant="outline" data-testid="action-staff-performance">
                  <Users className="h-4 w-4 mr-2" />
                  Staff Performance
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
